import type { ProgrammaticLandingPageConfig } from '../../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  SELF_CONSUMPTION_CAP,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from '../storageSizeFacts'

export const KWH_20_MAGAZYN_ENERGII: ProgrammaticLandingPageConfig = {
  slug: 'magazyn-energii-20-kwh-cena',
  lang: 'pl',
  topicId: 'kwh-speicher-20',
  title: 'Magazyn energii 20 kWh: cena i wartości orientacyjne 2026',
  metaDescription:
    'Ile kosztuje magazyn energii 20 kWh? Dla kogo się nadaje, wartości orientacyjne ' +
    'autokonsumpcji i bezpłatne porównanie ofert na instalację + magazyn.',
  keywords: [
    'magazyn energii 20 kWh cena',
    'magazyn energii 20 kWh koszt',
    'bateria fotowoltaiczna 20 kWh',
    'ile kosztuje magazyn energii 20 kWh',
  ],
  eyebrow: 'Wielkość magazynu: 20 kWh',
  h1: 'Magazyn energii 20 kWh: cena i wartości orientacyjne',
  intro:
    'Magazyn energii 20 kWh to nasza największa indeksowana wielkość - pomyślana dla dużych ' +
    'gospodarstw z pompą ciepła i samochodem elektrycznym jednocześnie, w połączeniu z ' +
    `referencyjną instalacją ${STORAGE_REFERENCE_KWP} kWp. Znajdziesz tu wartości orientacyjne ` +
    'dotyczące odpowiedniej wielkości gospodarstwa, autokonsumpcji z magazynem energii, a tuż ' +
    'poniżej kalkulator z już wypełnioną średnią ceną dla tego połączenia, obliczoną na podstawie ' +
    'prawdziwych ofert firm rzemieślniczych.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 20 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: 'Dla kogo odpowiedni jest magazyn energii 20 kWh?' },
    {
      type: 'paragraph',
      text:
        'Wartość orientacyjna: 20 kWh nadaje się dla dużych gospodarstw z pompą ciepła ORAZ ' +
        'samochodem elektrycznym i wysokim pożądanym stopniem samowystarczalności. Przy instalacji ' +
        `większej niż ${STORAGE_REFERENCE_KWP} kWp zwykle rośnie też sensowna do wykorzystania ` +
        'część magazynu - dokładną rekomendację dla Twojego dachu daje kalkulator doboru ' +
        'instalacji.',
    },
    { type: 'heading', text: 'Autokonsumpcja z magazynem energii' },
    {
      type: 'paragraph',
      text:
        `Bez magazynu udział autokonsumpcji w instalacji ${STORAGE_REFERENCE_KWP} kWp wynosi ` +
        `zwykle ok. ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %. Z magazynem energii udział ten ` +
        `rośnie w modelu obliczeniowym o ${SELF_CONSUMPTION_BATTERY_BONUS * 100} punktów ` +
        `procentowych, do ok. ${SELF_CONSUMPTION_WITH_BATTERY * 100} %, a z pompą ciepła i ` +
        `samochodem elektrycznym razem - aż do granicy ${SELF_CONSUMPTION_CAP * 100} % - powyżej ` +
        'tego punktu jeszcze większy magazyn niemal nie przynosi dodatkowej korzyści według modelu ' +
        'obliczeniowego. Dokładne obliczenie dla Twojego zużycia daje kalkulator doboru instalacji.',
    },
    { type: 'heading', text: 'Zasilanie awaryjne' },
    {
      type: 'paragraph',
      text:
        'Przy 20 kWh skrzynka backup do zasilania awaryjnego zwykle wystarcza na większą część ' +
        'domu przez dłuższy czas - dokładny czas zależy od rzeczywistego zużycia podłączonych ' +
        'urządzeń. Czy i w jakim zakresie dana oferta obejmuje zasilanie awaryjne, zależy od ' +
        'konkretnej firmy - warto o to zapytać bezpośrednio.',
    },
    { type: 'heading', text: 'Koszty magazynu i dofinansowanie' },
    {
      type: 'paragraph',
      text:
        'Koszty magazynów energii mocno się różnią w zależności od producenta i złożoności ' +
        'montażu - badanie rynku z 2026 roku pokazuje przedział od ok. ' +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.min} do ${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('pl-PL')} ` +
        '€ za kWh, co przy 20 kWh jest wyraźnie odczuwalne w całkowitym budżecie, dlatego dokładne ' +
        'porównanie cen szczególnie się opłaca. W Niemczech istnieją regionalne dofinansowania ' +
        'specjalnie na magazyny energii (np. program kraju związkowego Berlin z dopłatą do ' +
        'magazynów energii); w Austrii system EAG dolicza 150 €/kWh za magazyn - dokładne ' +
        'obliczenie dla Twojego połączenia daje kalkulator poniżej.',
    },
  ],
  faq: [
    {
      q: 'Dla kogo opłaca się magazyn energii 20 kWh?',
      a: 'Przede wszystkim dla dużych gospodarstw z pompą ciepła i samochodem elektrycznym jednocześnie oraz wysokim pożądanym stopniem samowystarczalności. Bez tych dużych odbiorników mniejsza wielkość jest zwykle bardziej opłacalna.',
    },
    {
      q: 'Czy magazyn większy niż 20 kWh przynosi dodatkowe korzyści?',
      a: 'Według modelu obliczeniowego niewiele - udział autokonsumpcji jest ograniczony do 85 %, niezależnie od wielkości magazynu. Od pewnego punktu koszty rosną szybciej niż dodatkowa korzyść.',
    },
    {
      q: 'Z jaką wielkością instalacji łączony jest zwykle magazyn 20 kWh?',
      a: `Na tej stronie jako punkt odniesienia przyjęto instalację ${STORAGE_REFERENCE_KWP} kWp, w praktyce często łączony też z 20 kWp lub więcej - dokładną rekomendację dla Twojego dachu daje kalkulator doboru instalacji.`,
    },
    {
      q: 'Czy 20 kWh wystarczy na pełną samowystarczalność w nocy?',
      a: 'Tak, to zwykle w pełni wystarcza nawet przy wysokim zużyciu (np. ładowanie samochodu elektrycznego i pompa ciepła razem). Dokładną ocenę dla Twojego rzeczywistego zużycia daje kalkulator doboru instalacji.',
    },
  ],
  primaryCta: { label: 'Poproś teraz o bezpłatną ofertę', action: 'customer' },
  secondaryCta: {
    label: 'Koszt instalacji fotowoltaicznej: pełny przewodnik',
    action: 'link',
    href: '/koszt-instalacji-fotowoltaicznej',
  },
  relatedLinks: [
    { label: 'Magazyn energii 15 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-15-kwh-cena' },
    { label: 'Magazyn energii 10 kWh: cena i wartości orientacyjne', href: '/magazyn-energii-10-kwh-cena' },
    { label: 'Instalacja fotowoltaiczna 20 kWp: cena i wartości orientacyjne', href: '/instalacja-fotowoltaiczna-20-kwp-cena' },
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
  ],
}
